import { useAction, useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { useTranslation } from '@root/shared/localization'
import { InfoMessage } from '@root/shared/ui-kit'

import { PremiumCurrencyDetailsContent } from './premium-currency-details-content'
import { PremiumCurrencyDetailsSkeleton } from './premium-currency-details-skeleton'

import type { PremiumCurrencyInfoModel } from '@root/features/premium/currency-information'

type Props = {
  model: PremiumCurrencyInfoModel
  onEmojiEdit: () => void
  onLogoEdit: () => void
  onBannerEdit: () => void
}

export const PremiumCurrencyDetails: React.FC<Props> = ({
  model,
  onEmojiEdit,
  onLogoEdit,
  onBannerEdit,
}) => {
  const { t } = useTranslation()

  const [state] = useAtom(model.pairState)

  const loadPair = useAction(model.loadPairAction)

  const renderContent = () =>
    AsyncStateN.matchWaiting(state, {
      Success: ({ pair, premiumMeta }) => (
        <Content>
          <PremiumCurrencyDetailsContent
            pair={pair}
            currency={premiumMeta.currency}
            premium={premiumMeta}
            onEditClick={onLogoEdit}
            onBannerEditClick={onBannerEdit}
            onEditEmoji={onEmojiEdit}
          />
        </Content>
      ),
      Error: () => (
        <ErrorContainer>
          <InfoMessage
            title={t('common.something-went-wrong')}
            intent="error"
            onRetry={() => loadPair()}
          />
        </ErrorContainer>
      ),
      Waiting: () => (
        <Content>
          <PremiumCurrencyDetailsSkeleton />
        </Content>
      ),
    })

  return <Root>{renderContent()}</Root>
}

const Root = styled.div`
  display: grid;
  grid-template-rows: minmax(0, 1fr);
  background: var(--background-1);
`

const Content = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  padding-block: 0 12px;
  padding-inline: 12px;
  overflow: auto;
`

const ErrorContainer = styled.div`
  display: grid;
  justify-content: center;
  align-items: center;
`
